import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { catalogItemDetailQueryOptions } from "./catalog-items";

// The Item Search island card's variant read (#798) — a different shape from
// the catalog card's, because its donor reads differently.
//
// The catalog card's donor reads the *picked sibling*, one per pick
// (`item-card.tsx`, `{ skip: isOwnItem }`), which `useCatalogVariantPreview`
// ports. The island's donor reads the card's *own* item on a press anywhere on
// the strip (`ItemCard.tsx:66-76`, `VariantTiles.tsx:49-67`), and that one
// response fills every member at once — so the first press spins, greys the
// CTA and the kebab whichever tile it lands on, and every switch after it is
// instant. Atlas's details are per item and carry no siblings' fields, so the
// press reads each sibling the strip offers instead: the same moment, the same
// one wait, the same instant switches afterwards.
//
// `preferCacheValue = true` is the donor's: an item already held is not read
// again. One whose read failed holds nothing, so the next press reads it again
// — which is what the donor's `ITEM_VARIANTS` snackbar tells the reader to do.

export interface ItemSearchVariantLoad {
  /** Read the siblings not yet held — the strip's press. */
  load: () => void;
  /** `isFetchingVariantsData`: a press's read is in flight. */
  loading: boolean;
  /** `isErrorVariantsData`: a sibling on the strip failed to load. */
  failed: boolean;
}

export function useItemSearchVariantLoad(siblingIds: readonly number[]): ItemSearchVariantLoad {
  const queryClient = useQueryClient();
  const [inFlight, setInFlight] = useState(0);
  const [pressed, setPressed] = useState(false);
  // Watched, never fetched: the strip's own reads are the press's, and these
  // observers only follow what the cache holds for each sibling.
  const siblings = useQueries({
    queries: siblingIds.map((id) => ({ ...catalogItemDetailQueryOptions(id), enabled: false })),
  });

  const load = () => {
    setPressed(true);
    const unread = siblingIds.filter(
      (id) => queryClient.getQueryData(catalogItemDetailQueryOptions(id).queryKey) === undefined,
    );
    if (unread.length === 0) return;
    setInFlight((count) => count + 1);
    // `fetchQuery` joins a read already in flight for the same item. Settled,
    // not `all`: the press's wait ends when its last read does, not at its
    // first failure while the others are still in flight.
    void Promise.allSettled(
      unread.map((id) =>
        // Held for the session (`gcTime: Infinity`), as the donor's object
        // cache holds a group it has read: a sibling nobody is observing would
        // otherwise be dropped after five minutes, and its card's next press
        // would wait on a read the donor never makes again.
        queryClient.fetchQuery({ ...catalogItemDetailQueryOptions(id), gcTime: Infinity }),
      ),
    ).finally(() => setInFlight((count) => count - 1));
  };

  const loading = inFlight > 0;
  return {
    load,
    loading,
    // Read off the cache rather than kept by hand, so overlapping presses
    // cannot leave a verdict behind: a sibling the strip offers whose last read
    // failed and left it nothing to draw. The donor's `isError` drops while a
    // retry reads, so the snackbar goes with the press and comes back only if
    // the retry fails too.
    failed:
      pressed &&
      !loading &&
      siblings.some((sibling) => sibling.isError && sibling.data === undefined),
  };
}
